<script setup lang="ts">
import { reactive, computed } from 'vue'
import { useResultModalStore } from '@/stores/resultModal'
import { useCreateUserMutation, useUpdateUserMutation } from '@/api/queries'
import type { UserSummary, UserGroup } from '@/types/api'

const props = defineProps<{ user: UserSummary | null }>()
const emit = defineEmits<{ close: []; saved: [] }>()

const resultModal = useResultModalStore()
const isEdit = computed(() => props.user !== null)

const form = reactive({
  email: props.user?.email ?? '',
  preferredUsername: props.user?.preferredUsername ?? '',
  group: (props.user?.group ?? 'General') as UserGroup,
})

const createMutation = useCreateUserMutation()
const updateMutation = useUpdateUserMutation()
const isSaving = computed(() => createMutation.isPending.value || updateMutation.isPending.value)

const _emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const canSave = computed(() => _emailRe.test(form.email.trim()) && form.preferredUsername.trim() !== '')

async function onSave() {
  if (!canSave.value) return
  try {
    if (isEdit.value && props.user) {
      await updateMutation.mutateAsync({
        username: props.user.username,
        req: {
          email: form.email.trim(),
          preferredUsername: form.preferredUsername.trim(),
          group: form.group,
        },
      })
      resultModal.show('ユーザー情報を更新しました。')
    } else {
      const created = await createMutation.mutateAsync({
        email: form.email.trim(),
        preferredUsername: form.preferredUsername.trim(),
        group: form.group,
      })
      // 市民革命で登録済みのアカウントには、審査Webの権限を追加するだけ(招待メールは送られない)
      resultModal.show(
        created?.existing
          ? '市民革命で登録済みのアカウントに、審査Webの権限を追加しました。招待メールは送られないため、市民革命と同じパスワードでログインできることを本人にお伝えください。'
          : 'ユーザーを登録しました。仮パスワードがメールで送信されます。',
      )
    }
    emit('saved')
  } catch (e) {
    resultModal.show((e as Error).message, 'ng')
  }
}
</script>

<template>
  <div class="bim-overlay" @click="emit('close')">
    <div class="bim-modal um-form-modal" @click.stop>
      <div class="bim-head">
        <div class="bim-title">{{ isEdit ? 'ユーザーを編集' : 'ユーザーを新規登録' }}</div>
        <button class="bim-close" @click="emit('close')">
          <svg class="icon" viewBox="0 0 24 24" style="width:18px;height:18px;"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <!-- 編集時、Cognito上のUsername(作成時のメールアドレス)は変更できない。
           メールアドレス欄を編集しても、これはあくまで email 属性が変わるだけで
           Username(内部識別子)自体は最初に作成した値のまま変わらない。 -->
      <div v-if="isEdit" class="um-form-note">
        ログインID(内部識別子): {{ props.user?.username }}
        <span class="um-form-note-sub">※メールアドレスを変更しても、このログインIDは変わりません</span>
      </div>

      <div class="edit-field">
        <label>メールアドレス</label>
        <input v-model="form.email" type="email" class="edit-input" placeholder="user@example.com" />
      </div>

      <div class="edit-field">
        <label>表示名</label>
        <input v-model="form.preferredUsername" type="text" class="edit-input" placeholder="セルモン(Salmon)" />
      </div>

      <div class="edit-field">
        <label>グループ</label>
        <select v-model="form.group" class="edit-input">
          <option value="General">一般(General)</option>
          <option value="Admin">管理者(Admin)</option>
        </select>
      </div>

      <div v-if="!isEdit" class="um-form-note">
        登録すると仮パスワードが本人のメールアドレス宛に送信されます(パスワードは管理者側では発行・把握しません)。
        <span class="um-form-note-sub">※市民革命で登録済みのメールアドレスの場合は、審査Webの権限を追加するだけで、メールは送られません</span>
      </div>
      <div v-if="isEdit && props.user?.alsoRevo" class="um-form-note um-form-note--warn">
        このユーザーは市民革命も利用しています。メールアドレス・表示名はアカウント共通のため、市民革命にも反映されます。
      </div>

      <div class="bim-foot">
        <button class="btn btn-ghost" @click="emit('close')">キャンセル</button>
        <button class="btn btn-primary" :disabled="!canSave || isSaving" @click="onSave">
          {{ isSaving ? '保存中…' : (isEdit ? '保存する' : '登録する') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.um-form-modal{width:min(94vw,420px);}
.um-form-note{
  font-size:11.5px;color:var(--ink-soft);line-height:1.6;background:var(--panel-alt);
  border-radius:8px;padding:8px 10px;
}
.um-form-note-sub{display:block;color:var(--ink-faint);margin-top:2px;}
.um-form-note--warn{background:#FEF3C7;color:#92400E;}
.bim-foot{display:flex;justify-content:flex-end;gap:10px;margin-top:6px;}
</style>
